/* ==========================================================================================
   EcoVessel 2026 · ev-footer.js · manners of the footer v2 (sections/ev-footer.liquid; look-motion builder,
   02-10-2026) and of the newsletter band (sections/ev-newsletter.liquid, the password page)
   1. Footer columns (<details open data-ev-foot-col>): open and fixed on tablets and computers, accordions on phones
      (closed until tapped). Without this script every column is simply open.
   2. The first time the wordmark comes into view it plays its entrance (letters settle, then the sunrise glint);
      its idle glint rests while it is off screen. Held only when the footer starts off screen; without this script,
      or with reduced motion, the logo is simply there.
   3. The forest drifts slowly as the page scrolls (parallax, [data-ev-foot-par]): a translate only, written on an
      animation frame and only while the forest is on screen; never with reduced motion. (Its own attribute: the
      generic [data-ev-parallax] of ev-main.js would move the whole strip.)
   4. The newsletter form: a wrong address is caught in place (aria-invalid + a message read out), the button shows it
      is busy while the form is sent; after a sign-up the thank-you is focused.
   5. "Back to top" ([data-ev-foot-top]): a smooth scroll to the top (instant with reduced motion), then the keyboard
      focus goes to the top of the page too. Without this script it is a plain link to the main content.
   Idempotent; re-binds on shopify:section:load.
   ========================================================================================== */
(() => {
  'use strict';
  const W = window, D = document;
  if (W.EVFooter) { W.EVFooter.init(D); return; }
  const phone = W.matchMedia('(max-width: 749px)');
  const reduce = W.matchMedia('(prefers-reduced-motion: reduce)');
  const each = (root, sel, fn) => { if (root.matches && root.matches(sel)) fn(root); root.querySelectorAll(sel).forEach(fn); };

  /* ---------- 1. columns ---------- */
  const syncCols = () => D.querySelectorAll('[data-ev-foot-col]').forEach((d) => {
    const s = d.querySelector('summary');
    if (!s) return;
    if (phone.matches) {
      if (!d.evTouched) d.open = false;
      s.removeAttribute('tabindex');
    } else {
      d.open = true;
      s.setAttribute('tabindex', '-1');         // nothing to toggle on a computer: no empty tab stop
    }
  });
  const bindCol = (d) => {
    if (d.evBound) return;
    d.evBound = 1;
    const s = d.querySelector('summary');
    if (!s) return;
    s.addEventListener('click', (e) => { if (!phone.matches) e.preventDefault(); else d.evTouched = 1; });
  };
  if (phone.addEventListener) phone.addEventListener('change', syncCols);

  /* ---------- 2. the wordmark's first look ---------- */
  const bindSeen = (foot) => {
    if (foot.evSeen || !('IntersectionObserver' in W)) return;
    foot.evSeen = 1;
    const svg = foot.querySelector('.ev-foot__logo .ev-logo');
    const brand = foot.querySelector('[data-ev-foot-brand]') || foot;
    const r = brand.getBoundingClientRect();
    const onScreen = r.top < W.innerHeight && r.bottom > 0;
    if (!reduce.matches && !onScreen) foot.classList.add('is-waiting');
    const io = new IntersectionObserver((ens) => ens.forEach((en) => {
      if (!en.isIntersecting) return;
      io.disconnect();
      foot.classList.add('is-seen');
      requestAnimationFrame(() => foot.classList.remove('is-waiting'));
    }), { threshold: 0.35 });
    io.observe(brand);
    if (svg) {
      new IntersectionObserver((ens) => ens.forEach((en) => svg.classList.toggle('ev-logo--paused', !en.isIntersecting))).observe(svg);
    }
  };

  /* ---------- 3. the forest drifts (parallax) ---------- */
  const bindParallax = (scene) => {
    if (scene.evPar || reduce.matches || !('IntersectionObserver' in W)) return;
    scene.evPar = 1;
    let on = false, ticking = false;
    const draw = () => {
      ticking = false;
      if (!on) return;
      const r = scene.getBoundingClientRect();
      const vh = W.innerHeight || 1;
      const p = Math.min(1, Math.max(0, (vh - r.top) / (vh + r.height)));   // 0 entering at the bottom, 1 leaving at the top
      const range = r.height * 0.24;                                         // the photo has 14% to spare above and below
      // a window onto a distant forest: the photo lags behind the page (it moves down inside the strip as you scroll)
      scene.style.setProperty('--ev-par', ((p - 0.5) * range).toFixed(1) + 'px');
    };
    const ask = () => { if (on && !ticking) { ticking = true; requestAnimationFrame(draw); } };
    new IntersectionObserver((ens) => ens.forEach((en) => { on = en.isIntersecting; ask(); })).observe(scene);
    W.addEventListener('scroll', ask, { passive: true });
    W.addEventListener('resize', ask, { passive: true });
  };
  if (reduce.addEventListener) reduce.addEventListener('change', () => D.querySelectorAll('[data-ev-foot-par]').forEach((s) => s.style.removeProperty('--ev-par')));

  /* ---------- 4. the newsletter form ---------- */
  const bindForm = (form) => {
    if (form.evBound) return;
    form.evBound = 1;
    const input = form.querySelector('input[type="email"]');
    const btn = form.querySelector('button[type="submit"]');
    const box = form.querySelector('[data-ev-news-error]');
    if (!input || !box) return;
    const say = (text) => {
      box.textContent = '';
      if (!text) { input.removeAttribute('aria-invalid'); return; }
      const icon = D.createElementNS('http://www.w3.org/2000/svg', 'svg');
      icon.setAttribute('class', 'ev-icon');
      icon.setAttribute('viewBox', '0 0 24 24');
      icon.setAttribute('aria-hidden', 'true');
      icon.innerHTML = '<circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2"/><path d="M12 7.5v5.5M12 16.5h.01" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>';
      const span = D.createElement('span');
      span.textContent = text;
      box.append(icon, span);
      input.setAttribute('aria-invalid', 'true');
    };
    form.addEventListener('submit', (e) => {
      input.value = input.value.trim();
      if (!input.value || !input.checkValidity()) {
        e.preventDefault();
        say(box.dataset.evMsg || 'Please enter a valid email address.');
        input.focus();
        return;
      }
      say('');
      if (btn) { btn.classList.add('is-loading'); btn.setAttribute('aria-busy', 'true'); }
    });
    input.addEventListener('input', () => { if (input.getAttribute('aria-invalid') && input.checkValidity() && input.value.trim()) say(''); });
  };
  const focusThanks = () => {
    const ok = D.querySelector('[data-ev-news-ok]');
    if (!ok || !/[?&]customer_posted=true/.test(W.location.search)) return;
    try { ok.focus({ preventScroll: false }); } catch (e) { ok.focus(); }
  };
  W.addEventListener('pageshow', () => D.querySelectorAll('[data-ev-news-form] .is-loading').forEach((b) => { b.classList.remove('is-loading'); b.removeAttribute('aria-busy'); }));

  /* ---------- 5. back to top ---------- */
  const bindTop = (a) => {
    if (a.evBound) return;
    a.evBound = 1;
    a.addEventListener('click', (e) => {
      e.preventDefault();
      try { W.scrollTo({ top: 0, behavior: reduce.matches ? 'auto' : 'smooth' }); } catch (err) { W.scrollTo(0, 0); }
      // the keyboard goes back to the top as well: the first link of the header (the logo or the menu)
      const first = D.querySelector('.shopify-section-group-header-group a[href], header a[href]');
      if (first) { try { first.focus({ preventScroll: true }); } catch (err) { /* old browser: focus without options scrolls, skip */ } }
    });
  };

  /* ---------- boot ---------- */
  const api = W.EVFooter = {
    init(root) {
      root = root || D;
      each(root, '[data-ev-foot-col]', bindCol);
      syncCols();
      each(root, '[data-ev-foot]', bindSeen);
      each(root, '[data-ev-foot-par]', bindParallax);
      each(root, '[data-ev-news-form]', bindForm);
      each(root, '[data-ev-foot-top]', bindTop);
    }
  };
  const boot = () => { api.init(D); focusThanks(); };
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', boot); else boot();
  D.addEventListener('shopify:section:load', (e) => api.init(e.target));
})();
